/* eslint-disable react-refresh/only-export-components */
import {
  useMemo,
  useCallback,
  InputHTMLAttributes,
  FC,
  memo,
  ChangeEvent,
} from 'react';

import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { MentionConfig } from 'ckeditor5';

import MentionableInput from './MentionableInput';
import MentionableTextarea from '../Textarea';

interface PROPS
  extends Omit<InputHTMLAttributes<HTMLTextAreaElement>, 'size' | 'onChange'> {
  size?: SIZE;
  mentionOptions: string[];
  subText?: string;
  label?: string | JSX.Element;
  information?: string;
  onSetFieldValue?: (field: string, value: string) => void;
  textType?: 'default' | 'danger' | 'success' | 'warning';
  rows?: number;
  name: string;
  onChange?: (name: string, value: string) => void;
  hideToolbar?: boolean;
  markdown?: boolean;
}

const Mention: FC<PROPS> = ({
  type,
  name,
  label,
  information,
  required,
  onChange,
  onSetFieldValue,
  mentionOptions,
  size,
  subText,
  placeholder,
  disabled,
  textType,
  rows,
  hideToolbar,
  value,
  markdown,
}) => {
  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> | string) => {
      if (onSetFieldValue) {
        onSetFieldValue(name, typeof e === 'string' ? e : e?.target?.value);
        return;
      }
      if (onChange) {
        onChange(name, typeof e === 'string' ? e : e?.target?.value);
      }
    },
    [name, onChange, onSetFieldValue],
  );

  const commonProps = useMemo(
    () => ({
      label: `${label} (@ to mention)`,
      className: cn('w-full'),
      name,
      information,
      required,
      size,
      textType,
      subText,
      placeholder,
      disabled,
      value: value as string,
    }),
    [
      disabled,
      information,
      label,
      name,
      placeholder,
      required,
      size,
      subText,
      textType,
      value,
    ],
  );

  const textAreaProps = useMemo(
    () => ({ ...(rows && { rows }), ...(hideToolbar && { hideToolbar }) }),
    [rows, hideToolbar],
  );

  const inputProps = useMemo(() => ({}), []);

  const mentionConfig = useMemo<MentionConfig>(
    () => ({
      feeds: [
        {
          marker: '@',
          feed: mentionOptions.map((item) => ({ id: item, text: item })),
        },
      ],
    }),
    [mentionOptions],
  );

  const componentToRender = useMemo(() => {
    switch (type) {
      case 'textarea':
        return (
          <MentionableTextarea
            mention={mentionConfig}
            richText
            {...commonProps}
            {...textAreaProps}
            onChange={handleOnChange}
            markdown={markdown}
          />
        );
      case 'input':
        return (
          <MentionableInput
            mentionItems={mentionConfig}
            {...commonProps}
            {...inputProps}
            onChange={handleOnChange}
          />
        );
      default:
        return (
          <MentionableInput
            mentionItems={mentionConfig}
            {...commonProps}
            {...inputProps}
            onChange={handleOnChange}
          />
        );
    }
  }, [
    commonProps,
    inputProps,
    textAreaProps,
    type,
    handleOnChange,
    mentionConfig,
    markdown,
  ]);

  return (
    <div>
      <div className="relative w-full">
        <div className="relative">{componentToRender}</div>
      </div>
    </div>
  );
};

export default memo(Mention);
